import { sameRegion } from '../../constants/requestRegions';
import { Request } from '../../types';
import { User, AppRole } from '../../types/auth';
import { RequestStage } from '../../types/stages';
import { extractViewerDomain } from '../../utils/partnerViewerScope';
import { ApprovalGroupCurrent } from '../approvalGroupsService';

export type CanAccessRequestOptions = {
  approvalGroup?: ApprovalGroupCurrent | null;
};

/**
 * Service to check if a user has authorization to access a specific request
 * This mirrors the backend authorization logic for additional frontend security
 */
export class RequestAuthService {
  /**
   * Check if a user can access a specific request based on their roles and request details
   */
  canAccessRequest(
    user: User | null,
    request: Request | null,
    options?: CanAccessRequestOptions,
  ): boolean {
    if (!user || !request) {
      return false;
    }

    // Check each role and apply the authorization logic
    for (const role of user.roles) {
      switch (role) {
        case AppRole.REQUESTOR:
        case AppRole.EXTERNAL_REQUESTOR:
          // Can access requests where they are the requestor (case-insensitive)
          if (
            request.requestorEmail?.toLowerCase() === user.email?.toLowerCase()
          ) {
            return true;
          }
          break;

        case AppRole.VERTIV_SPONSOR:
          // Can access requests where they are assigned as a sponsor
          if (this.isUserInTeamRole(request, user.email, 'vertiv_sponsor')) {
            return true;
          }
          break;

        case AppRole.DEFAULT_SPONSOR_NAM:
          // Can access requests where they are assigned as a default NAM sponsor
          if (
            this.isUserInTeamRole(request, user.email, 'default_sponsor_nam')
          ) {
            return true;
          }
          break;

        case AppRole.DEFAULT_SPONSOR_EMEA:
          // Can access requests where they are assigned as a default EMEA sponsor
          if (
            this.isUserInTeamRole(request, user.email, 'default_sponsor_emea')
          ) {
            return true;
          }
          break;

        case AppRole.DEFAULT_SPONSOR_APAC:
          // Can access requests where they are assigned as a default APAC sponsor
          if (
            this.isUserInTeamRole(request, user.email, 'default_sponsor_apac')
          ) {
            return true;
          }
          break;

        case AppRole.VERTIV_CONTRIBUTOR:
          // Can access requests where they are assigned as a contributor
          if (
            this.isUserInTeamRole(request, user.email, 'vertiv_contributor')
          ) {
            return true;
          }
          break;

        case AppRole.PARTNER_CONTRIBUTOR:
          // Can access requests where they are assigned as a partner contributor
          if (
            this.isUserInTeamRole(request, user.email, 'partner_contributor')
          ) {
            return true;
          }
          break;

        case AppRole.SUPERVISOR_NAM:
          // Can access North America requests
          if (this.isRequestInRegion(request, 'NAM')) {
            return true;
          }
          break;

        case AppRole.SUPERVISOR_EMEA:
          // Can access Europe/Middle East/Africa requests
          if (this.isRequestInRegion(request, 'EMEA')) {
            return true;
          }
          break;

        case AppRole.SUPERVISOR_APAC:
          // Can access Asia-Pacific requests
          if (this.isRequestInRegion(request, 'APAC')) {
            return true;
          }
          break;

        case AppRole.CONFIG_ENGINEER:
          // Config engineers can access all requests for technical review
          return true;

        case AppRole.BUSINESS_OWNER:
          // Business owners can access all requests for business oversight
          return true;

        case AppRole.IT_ADMINISTRATOR:
          // IT admins can access all requests
          return true;

        default:
          // Unknown role - no access
          break;
      }
    }

    if (
      this.canAccessViaAwaitingApprovalMembership(
        user,
        request,
        options?.approvalGroup,
      )
    ) {
      return true;
    }

    return false;
  }

  /**
   * Story 2.1 / AD-10: live Approvals group member may view peer requests
   * in AWAITING_APPROVAL on the same vendor host (view-only; stamp rules are separate).
   */
  private canAccessViaAwaitingApprovalMembership(
    user: User,
    request: Request,
    approvalGroup?: ApprovalGroupCurrent | null,
  ): boolean {
    if (!approvalGroup?.enabled || (approvalGroup.members?.length ?? 0) === 0) {
      return false;
    }

    const stage = request.sysRequestStage ?? request.stage;
    if (stage !== RequestStage.AWAITING_APPROVAL) {
      return false;
    }

    const actorUserId = user.userId;
    if (actorUserId == null) {
      return false;
    }

    if (!approvalGroup.members.some(member => member.userId === actorUserId)) {
      return false;
    }

    const requestorHost = extractViewerDomain(request.requestorEmail);
    const viewerHost = extractViewerDomain(user.email);
    if (!requestorHost || !viewerHost || requestorHost !== viewerHost) {
      return false;
    }

    return true;
  }

  /**
   * Check if user is assigned to a request in a specific team role
   */
  private isUserInTeamRole(
    request: Request,
    userEmail: string,
    teamRole: string,
  ): boolean {
    // Check if user is in the appropriate team array based on role (case-insensitive)
    const emailLower = userEmail?.toLowerCase();
    switch (teamRole) {
      case 'vertiv_sponsor':
      case 'default_sponsor_nam':
      case 'default_sponsor_emea':
      case 'default_sponsor_apac':
        // All sponsor roles (including default sponsors) check the vertivSponsors array
        return (
          Array.isArray(request.vertivSponsors) &&
          request.vertivSponsors.some((sponsor: any) =>
            typeof sponsor === 'string'
              ? sponsor.toLowerCase() === emailLower
              : sponsor.email?.toLowerCase() === emailLower,
          )
        );

      case 'vertiv_contributor':
        return (
          Array.isArray(request.vertivContributors) &&
          request.vertivContributors.some((contributor: any) =>
            typeof contributor === 'string'
              ? contributor.toLowerCase() === emailLower
              : contributor.email?.toLowerCase() === emailLower,
          )
        );

      case 'partner_contributor':
        return (
          Array.isArray(request.partnerContributors) &&
          request.partnerContributors.some((contributor: any) =>
            typeof contributor === 'string'
              ? contributor.toLowerCase() === emailLower
              : contributor.email?.toLowerCase() === emailLower,
          )
        );

      default:
        return false;
    }
  }

  /**
   * Check if request belongs to a specific region
   */
  private isRequestInRegion(request: Request, region: string): boolean {
    // ⚠️ `regionName` IS GONE (#1507). It was a second OR-arm that could only
    // ever GRANT, reading a field that is on no type, written by nothing, and
    // referenced nowhere else in the product. A phantom that can only widen an
    // authorization check is worth deleting on sight.
    //
    // The comparison now uses the same `sameRegion` the server does, so the
    // client gate and the server gate understand the same four spellings.
    return sameRegion(request.region, region);
  }

  /**
   * Get a user-friendly error message for authorization failures
   */
  getAccessDeniedMessage(user: User | null, request: Request | null): string {
    if (!user) {
      return 'You must be logged in to access this request.';
    }

    if (!request) {
      return 'Request not found or you do not have permission to access it.';
    }

    // Provide role-specific guidance
    const hasRequestorRole =
      user.roles.includes(AppRole.REQUESTOR) ||
      user.roles.includes(AppRole.EXTERNAL_REQUESTOR);
    const hasSponsorRole =
      user.roles.includes(AppRole.VERTIV_SPONSOR) ||
      user.roles.includes(AppRole.DEFAULT_SPONSOR_NAM) ||
      user.roles.includes(AppRole.DEFAULT_SPONSOR_EMEA) ||
      user.roles.includes(AppRole.DEFAULT_SPONSOR_APAC);
    const hasContributorRole =
      user.roles.includes(AppRole.VERTIV_CONTRIBUTOR) ||
      user.roles.includes(AppRole.PARTNER_CONTRIBUTOR);
    const hasSupervisorRole = user.roles.some(role =>
      [
        AppRole.SUPERVISOR_NAM,
        AppRole.SUPERVISOR_EMEA,
        AppRole.SUPERVISOR_APAC,
      ].includes(role),
    );
    const hasConfigEngineerRole = user.roles.includes(AppRole.CONFIG_ENGINEER);
    const hasBusinessOwnerRole = user.roles.includes(AppRole.BUSINESS_OWNER);

    if (hasRequestorRole) {
      return 'You can only access requests where you are the requestor.';
    }

    if (hasSponsorRole) {
      return 'You can only access requests where you are assigned as a sponsor.';
    }

    if (hasContributorRole) {
      return 'You can only access requests where you are assigned as a contributor.';
    }

    if (hasSupervisorRole) {
      return 'You can only access requests in your assigned region.';
    }

    if (hasConfigEngineerRole) {
      return 'You should have access to all requests for technical review. Please contact your administrator if you believe this is an error.';
    }

    if (hasBusinessOwnerRole) {
      return 'You should have access to all requests for business oversight. Please contact your administrator if you believe this is an error.';
    }

    return 'You do not have permission to access this request. Please contact your administrator if you believe this is an error.';
  }
}

// Export singleton instance
export const requestAuthService = new RequestAuthService();
